/**
 * Parks → Play Circle one-time park handoff (RC1).
 * Safe snapshot only — never carries full Parks list payloads.
 *
 * Playdate meeting parks must come from the app’s canonical nearby list
 * (same origin as Home / Parks / Picks). SF fixture parks are preview-only.
 */

import type { ParkContext } from '@/types/parks'
import {
  PLAYDATE_PARK_FIXTURES,
  type PlaydateParkFixture,
} from '@/lib/dev-preview-play/playdateBuilderFixtures'

export const PLAYDATE_FIXTURE_PARK_IDS = new Set(
  PLAYDATE_PARK_FIXTURES.map((p) => p.id)
)

/** Nearby park row already resolved for the canonical selected location. */
export type NearbyPlaydateParkInput = {
  id: string
  name: string
  address?: string | null
  distanceMiles?: number | null
}

/** Builder park option — nearby parks, optional handoff seed, or preview fixtures. */
export type PlaydateParkOption = {
  id: string
  name: string
  descriptor: string
  /** Prisma Park.id when proposable; null → customLocationLabel only. */
  canonicalParkId: string | null
  source: 'fixture' | 'handoff' | 'nearby'
}

export type BuildPlaydateParkOptionsOpts = {
  /** Parks near the app’s canonical selected location (production path). */
  nearbyParks?: readonly NearbyPlaydateParkInput[] | null
  /**
   * Preview/dev only. When true and nearby is empty, fall back to SF fixtures.
   * Must stay false in production app-beta.
   */
  allowFixtures?: boolean
}

export type PlayParkHandoffSeed = {
  /** Stable key for one-shot consume (parkId or name). */
  consumeKey: string
  parkName: string
  parkAddress: string | null
  parkLat: number | null
  parkLng: number | null
  parkSource: string | null
  /** Raw parks list id — may be DB id, place id, or null. */
  parkId: string | null
}

/** Normalize parent ParkContext into a local seed, or null if unusable. */
export function normalizePlayParkHandoff(
  context: ParkContext | null | undefined
): PlayParkHandoffSeed | null {
  if (!context) return null
  const parkName = (context.parkName ?? '').trim()
  if (!parkName) return null
  const parkIdRaw =
    typeof context.parkId === 'string' && context.parkId.trim()
      ? context.parkId.trim()
      : null
  // Never treat fixture slugs as DB ids if somehow passed through.
  const parkId =
    parkIdRaw && !PLAYDATE_FIXTURE_PARK_IDS.has(parkIdRaw) ? parkIdRaw : null
  const consumeKey = parkId ?? `name:${parkName.toLowerCase()}`
  return {
    consumeKey,
    parkName,
    parkAddress:
      typeof context.parkAddress === 'string' && context.parkAddress.trim()
        ? context.parkAddress.trim()
        : null,
    parkLat: typeof context.parkLat === 'number' ? context.parkLat : null,
    parkLng: typeof context.parkLng === 'number' ? context.parkLng : null,
    parkSource:
      typeof context.parkSource === 'string' && context.parkSource.trim()
        ? context.parkSource.trim()
        : null,
    parkId,
  }
}

export function handoffSeedToParkOption(
  seed: PlayParkHandoffSeed
): PlaydateParkOption {
  const id = seed.parkId ?? `handoff:${slugifyParkName(seed.parkName)}`
  const descriptor = seed.parkAddress?.trim() || 'From Parks'
  return {
    id,
    name: seed.parkName,
    descriptor,
    canonicalParkId: seed.parkId,
    source: 'handoff',
  }
}

function slugifyParkName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'park'
}

export function fixtureToParkOption(
  fixture: PlaydateParkFixture
): PlaydateParkOption {
  return {
    id: fixture.id,
    name: fixture.name,
    descriptor: fixture.descriptor,
    canonicalParkId: null,
    source: 'fixture',
  }
}

/**
 * Map a nearby Places row into a builder option.
 * Nearby ids are Google place ids — never pass them as Prisma parkId.
 */
export function nearbyParkToParkOption(
  park: NearbyPlaydateParkInput
): PlaydateParkOption {
  const rawId = String(park.id ?? '').trim()
  const name = (park.name ?? '').trim() || 'Park'
  const id = rawId ? `nearby:${rawId}` : `nearby:${slugifyParkName(name)}`
  const address = park.address?.trim() || null
  const distance =
    typeof park.distanceMiles === 'number' && Number.isFinite(park.distanceMiles)
      ? park.distanceMiles
      : null
  const descriptor =
    address ||
    (distance != null ? `${distance.toFixed(1)} mi away` : 'Near your selected location')
  return {
    id,
    name,
    descriptor,
    canonicalParkId: null,
    source: 'nearby',
  }
}

function dedupeParkOptions(
  options: readonly PlaydateParkOption[]
): PlaydateParkOption[] {
  const seenIds = new Set<string>()
  const seenNames = new Set<string>()
  const out: PlaydateParkOption[] = []
  for (const option of options) {
    const nameKey = option.name.trim().toLowerCase()
    if (seenIds.has(option.id) || seenNames.has(nameKey)) continue
    seenIds.add(option.id)
    seenNames.add(nameKey)
    out.push(option)
  }
  return out
}

/**
 * Build playdate meeting-place options from canonical nearby parks.
 * Handoff park (explicit Parks → Play) stays first when present.
 * SF fixtures only when `allowFixtures` is true (preview) and nearby is empty.
 */
export function buildPlaydateParkOptions(
  seed: PlayParkHandoffSeed | null,
  opts?: BuildPlaydateParkOptionsOpts
): PlaydateParkOption[] {
  const nearby = (opts?.nearbyParks ?? [])
    .filter((p) => Boolean((p.name ?? '').trim()))
    .map(nearbyParkToParkOption)

  const allowFixtures = opts?.allowFixtures === true
  const fixtures =
    allowFixtures && nearby.length === 0
      ? PLAYDATE_PARK_FIXTURES.map(fixtureToParkOption)
      : []

  const base = nearby.length > 0 ? nearby : fixtures
  if (!seed) return dedupeParkOptions(base)

  const handoff = handoffSeedToParkOption(seed)
  return dedupeParkOptions([handoff, ...base])
}

export function resolvePlaydateParkOption(
  parkId: string | null | undefined,
  options: readonly PlaydateParkOption[]
): PlaydateParkOption | null {
  if (!parkId) return null
  return options.find((p) => p.id === parkId) ?? null
}

/**
 * Decide auto-open vs contextual wait after handoff is consumed into local seed.
 * - 1 friend: auto-open builder (unambiguous)
 * - else: keep seed for next Plan CTA
 */
export function resolveParkHandoffOpenMode(friendCount: number): 'auto_builder' | 'seed_only' {
  return friendCount === 1 ? 'auto_builder' : 'seed_only'
}

/**
 * Initial wizard step when opening builder with a preselected park.
 * Skip park step when seed already selected.
 */
export function resolveBuilderStartStep(hasPreselectedPark: boolean): 'dogs' | 'date' {
  return hasPreselectedPark ? 'date' : 'dogs'
}
